/**
 * MINI_FIX_BU — Konsolide PAJE Özeti panel.
 * MINI_FIX_BV — net-effect lines + grand heading + dynamic type ordering
 *               + reconciliation card.
 *
 * Forest-view counterpart to the Yevmiye tab's `JournalYearlySummary`:
 * a single collapsible card that shows the auditor the consolidated
 * Dr/Cr effect of every PAJE on screen, by type and in grand total.
 *
 * Critical reuse: `AggregatedPaje.suggestedJE` is `AggregatedJELine[]` —
 * the IDENTICAL shape `consolidateJEs` consumes via `je.lines`. So we
 * adapt each PAJE to `{ lines: paje.suggestedJE }` and call the existing
 * shared consolidation engine + the shared `ConsolidatedSummaryTable`
 * directly. No new aggregation logic, no new table chrome.
 *
 * BV — debit/credit sign convention (the heart of the panel):
 *   • A debit balance on a P&L / equity account = expense / loss =
 *     reduces profit / equity = NEGATIVE audit effect.
 *   • A credit balance on a P&L / equity account = income / gain =
 *     increases profit / equity = POSITIVE audit effect.
 *   Net = signed sum of `paje.variance` (MINI_FIX_BR sealed this sign).
 *   We use the SAME `sumDecimalStrings` source the page header uses, so
 *   `grand-net` always agrees with the header "Toplam Variance". We do
 *   NOT classify accounts ourselves (categorizeAccount lumps Retained
 *   Earnings + FX Loss into BALANCE_SHEET — line-level reclassification
 *   would be wrong; the verified-correct net is the variance sum).
 *
 * Behaviour:
 *   • Default OPEN — the forest view is the auditor's first want, same
 *     as `JournalYearlySummary`.
 *   • Per-type subtotals in DYNAMIC order: preferred types first
 *     (FX_REVALUATION / ADJUSTMENT / RECLASSIFICATION), then any other
 *     types actually present in the data (future-proof for new types).
 *   • Each per-type AND the grand-total table is followed by a net-effect
 *     line: `formatSigned(net)` + a plain debit/credit clause ("net borç
 *     etkisi — kârı/özkaynağı azaltır" / "net alacak etkisi — artırır").
 *   • GRAND TOTAL has a distinct Sigma + heading row so it never reads
 *     as a fourth type.
 *   • Below the grand table, a "Mutabakat" card bridges gross posting to
 *     net audit effect in plain language (HONEST: no fake "gross − x =
 *     net" subtraction; just gross context + per-type nets + grand net +
 *     explanatory note).
 *   • Always reflects the FULL `pajes` array — the PajesTab toolbar
 *     type filter is for the card list below, NOT this overview.
 */

import { ChevronDown, ChevronRight, Sigma } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PajeTypeBadge } from "@/features/engagement/components/PajeTypeBadge";
import { Card } from "@/components/ui/card";
import { sumDecimalStrings } from "@/features/export/helpers/sumDecimalStrings";
import { formatCurrency, formatSigned } from "@/lib/format";
import {
  ConsolidatedSummaryTable,
  consolidateJEs,
  type TFn,
} from "@/pages/engagement/tabs/journalEntryComponents";
import type {
  AggregatedJELine,
  AggregatedJournalEntry,
  AggregatedPaje,
  AggregatedPajeType,
} from "@/types";

export interface PajeConsolidatedSummaryProps {
  pajes: AggregatedPaje[];
  currency: string;
}

const PREFERRED: AggregatedPajeType[] = [
  "FX_REVALUATION",
  "ADJUSTMENT",
  "RECLASSIFICATION",
];

/**
 * Minimal structural shape `consolidateJEs` actually consumes — it only
 * reads `je.lines`. Casting through this avoids `any` while letting us
 * feed PAJE `suggestedJE` arrays into the shared consolidation engine.
 */
type JELineCarrier = { lines: AggregatedJELine[] };

function asEntries(pajes: AggregatedPaje[]): AggregatedJournalEntry[] {
  return pajes.map<JELineCarrier>((p) => ({
    lines: p.suggestedJE,
  })) as unknown as AggregatedJournalEntry[];
}

/**
 * Signed sum of variances, in the equity-effect convention sealed by
 * MINI_FIX_BR. `sumDecimalStrings` handles negative strings cleanly, so
 * the returned signed decimal string is the authoritative net (and
 * agrees with the page header's Toplam Variance by construction).
 */
function netVariance(pajes: AggregatedPaje[]): string {
  return sumDecimalStrings(pajes.map((p) => p.variance));
}

/**
 * BV — render a net-effect line under any of the consolidated tables.
 * Signed amount via `formatSigned` (parens + destructive for negatives)
 * followed by a plain debit/credit clause that names the direction in
 * words. Never show a bare signed number without the clause.
 */
function NetEffectLine({
  net,
  currency,
  testId,
  t,
}: {
  net: string;
  currency: string;
  testId: string;
  t: TFn;
}) {
  const isCharge = Number(net) < 0;
  const clauseKey = isCharge
    ? "paje.summary.netCharge"
    : "paje.summary.netGain";
  return (
    <p
      className="text-sm flex flex-wrap items-baseline gap-x-2"
      data-testid={testId}
    >
      <span className="text-muted-foreground">
        {t("paje.summary.netEffect")}:
      </span>
      <span className="font-mono font-semibold">
        {formatSigned(net, currency)}
      </span>
      <span className="text-xs text-muted-foreground">— {t(clauseKey)}</span>
    </p>
  );
}

export function PajeConsolidatedSummary({
  pajes,
  currency,
}: PajeConsolidatedSummaryProps) {
  const { t } = useTranslation();
  const tDyn = t as unknown as TFn;
  const [open, setOpen] = useState(true);

  // Group once; subtotal tables consume the buckets, grand total consumes
  // the full `pajes` array. useMemo so a re-render that doesn't change
  // the inputs doesn't redo the linear walk.
  const buckets = useMemo(() => {
    const out = new Map<AggregatedPajeType, AggregatedPaje[]>();
    for (const p of pajes) {
      const bucket = out.get(p.type);
      if (bucket) bucket.push(p);
      else out.set(p.type, [p]);
    }
    return out;
  }, [pajes]);

  // BV Part 3 — dynamic type order: preferred first, then any other types
  // actually present in the data so a brand-new PAJE type gets its own
  // subtotal automatically.
  const orderedTypes = useMemo<AggregatedPajeType[]>(
    () => [
      ...PREFERRED.filter((t) => buckets.has(t)),
      ...Array.from(buckets.keys()).filter(
        (t) => !PREFERRED.includes(t),
      ),
    ],
    [buckets],
  );

  const grandSummary = useMemo(
    () => consolidateJEs(asEntries(pajes), tDyn),
    [pajes, tDyn],
  );
  const grandNet = useMemo(() => netVariance(pajes), [pajes]);

  if (pajes.length === 0) return null;

  return (
    <Card
      className="p-0 overflow-hidden"
      data-testid="paje-consolidated-summary"
    >
      <button
        type="button"
        className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="paje-consolidated-summary-trigger"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-base font-semibold">
          {t("paje.summary.title")}
        </span>
        <span className="text-sm text-muted-foreground">
          · {t("paje.summary.subtitle")}
        </span>
      </button>

      {open && (
        <div
          className="px-4 pb-4 space-y-6"
          data-testid="paje-consolidated-summary-content"
        >
          {orderedTypes.map((type) => {
            const ofType = buckets.get(type);
            if (!ofType || ofType.length === 0) return null;
            const typeLabel = tDyn(`paje.type.${type}`);
            const typeSummary = consolidateJEs(asEntries(ofType), tDyn);
            const typeNet = netVariance(ofType);
            return (
              <section
                key={type}
                className="space-y-2"
                data-testid={`paje-summary-type-${type}-section`}
              >
                <div className="flex items-center gap-2">
                  <PajeTypeBadge type={type} />
                  <span className="text-xs text-muted-foreground">
                    {tDyn("paje.summary.typeCount", { count: ofType.length })}
                  </span>
                </div>
                <ConsolidatedSummaryTable
                  summary={typeSummary}
                  totalLabel={tDyn("paje.summary.typeTotal", {
                    type: typeLabel,
                  })}
                  currency={currency}
                  testIdPrefix={`paje-summary-type-${type}`}
                />
                <NetEffectLine
                  net={typeNet}
                  currency={currency}
                  testId={`paje-summary-type-${type}-net`}
                  t={tDyn}
                />
              </section>
            );
          })}

          <div
            className="pt-4 border-t border-border space-y-2"
            data-testid="paje-summary-grand-section"
          >
            {/* BV Part 2 — distinct grand-total heading (Sigma icon, not a
                PajeTypeBadge) so the auditor never reads it as a 4th type. */}
            <div className="flex items-center gap-2">
              <Sigma
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <span className="text-sm font-semibold">
                {t("paje.summary.grandHeading")}
              </span>
            </div>
            <ConsolidatedSummaryTable
              summary={grandSummary}
              totalLabel={t("paje.summary.grandTotal")}
              currency={currency}
              testIdPrefix="paje-summary-grand"
            />
            <NetEffectLine
              net={grandNet}
              currency={currency}
              testId="paje-summary-grand-net"
              t={tDyn}
            />
          </div>

          {/* BV Part 4 — Mutabakat card. Bridges gross (Dr=Cr posting) to
              the net audit effect in plain debit/credit language. HONEST:
              no fake `gross − x = net` arithmetic; just gross context +
              per-type net rows + grand net + a teaching note. */}
          <ReconciliationCard
            pajes={pajes}
            orderedTypes={orderedTypes}
            buckets={buckets}
            grossDebit={grandSummary.totalDebit}
            grandNet={grandNet}
            currency={currency}
            t={tDyn}
          />
        </div>
      )}
    </Card>
  );
}

/**
 * BV Part 4 — Mutabakat (gross posting → net audit effect) card.
 *
 * Intentionally bordered + visually distinct from the consolidated
 * tables above. Each row is shown in plain debit/credit language so any
 * reader (senior auditor or trainee) can follow why gross exceeds net.
 * The per-type nets sum to grandNet by construction (sumDecimalStrings
 * is associative over the variance set).
 */
function ReconciliationCard({
  pajes,
  orderedTypes,
  buckets,
  grossDebit,
  grandNet,
  currency,
  t,
}: {
  pajes: AggregatedPaje[];
  orderedTypes: AggregatedPajeType[];
  buckets: Map<AggregatedPajeType, AggregatedPaje[]>;
  grossDebit: number;
  grandNet: string;
  currency: string;
  t: TFn;
}) {
  if (pajes.length === 0) return null;
  return (
    <section
      className="rounded-md border border-border bg-muted/20 p-4 space-y-3"
      data-testid="paje-summary-recon"
    >
      <h4 className="text-sm font-semibold">{t("paje.summary.recon.title")}</h4>

      <div
        className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm"
        data-testid="paje-summary-recon-gross"
      >
        <span className="text-muted-foreground">
          {t("paje.summary.recon.gross")}
        </span>
        <span className="font-mono">
          {formatCurrency(grossDebit.toFixed(2), currency)}{" "}
          <span
            className="text-emerald-600 dark:text-emerald-400"
            aria-hidden="true"
          >
            ✓
          </span>
        </span>
      </div>

      <div className="space-y-1.5">
        {orderedTypes.map((type) => {
          const ofType = buckets.get(type) ?? [];
          if (ofType.length === 0) return null;
          const typeNet = netVariance(ofType);
          const isCharge = Number(typeNet) < 0;
          const clauseKey = isCharge
            ? "paje.summary.netCharge"
            : "paje.summary.netGain";
          const typeLabel = t(`paje.type.${type}`);
          return (
            <div
              key={type}
              className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm"
              data-testid={`paje-summary-recon-type-${type}`}
            >
              <span className="text-muted-foreground">{typeLabel}</span>
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono">
                  {formatSigned(typeNet, currency)}
                </span>
                <span className="text-xs text-muted-foreground">
                  — {t(clauseKey)}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <div
        className="pt-2 border-t border-border flex flex-wrap items-baseline justify-between gap-x-3 text-sm"
        data-testid="paje-summary-recon-net"
      >
        <span className="font-semibold">
          {t("paje.summary.recon.netEffect")}
        </span>
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono font-semibold">
            {formatSigned(grandNet, currency)}
          </span>
          <span className="text-xs text-muted-foreground">
            —{" "}
            {Number(grandNet) < 0
              ? t("paje.summary.netCharge")
              : t("paje.summary.netGain")}
          </span>
        </span>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        {t("paje.summary.recon.note")}
      </p>
    </section>
  );
}
